import { useState, type FormEvent } from 'react'
import { ActionGlyph } from '../../components/ActionGlyph'
import type { ActionResult } from '../../domain/models'
import { focusFirstInvalid } from '../../lib/forms'
import { useAppActions } from '../../state/AppProvider'
import { AccountScaffold, InlineStatus } from './AccountScaffold'

interface LoginPageProps {
  navigate: (to: string) => void
}

interface LoginErrors {
  email?: string
  password?: string
}

export function LoginPage({ navigate }: LoginPageProps) {
  const actions = useAppActions()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [remember, setRemember] = useState(true)
  const [errors, setErrors] = useState<LoginErrors>({})
  const [result, setResult] = useState<ActionResult | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    setResult(null)
    const nextErrors: LoginErrors = {}
    if (!emailPattern.test(email.trim())) nextErrors.email = 'Enter a complete email address.'
    if (!password) nextErrors.password = 'Enter your password.'
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      focusFirstInvalid(form)
      return
    }
    setSubmitting(true)
    try {
      const nextResult = await actions.login({ email: email.trim(), password, remember })
      setResult(nextResult)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AccountScaffold
      eyebrow="Account · Welcome back"
      title="Sign in"
      intro="Pick up your connected ticket, itinerary, Community, and passport where you left them."
      onBack={() => navigate('/welcome')}
    >
      <form className="access-form" noValidate onSubmit={handleSubmit} aria-busy={submitting}>
        <div className="access-field">
          <label htmlFor="login-email">Email</label>
          <input
            autoCapitalize="none"
            autoComplete="email"
            id="login-email"
            inputMode="email"
            spellCheck={false}
            type="email"
            value={email}
            aria-describedby={errors.email ? 'login-email-error' : undefined}
            aria-invalid={Boolean(errors.email)}
            onChange={(event) => {
              setEmail(event.target.value)
              if (errors.email) setErrors((current) => ({ ...current, email: undefined }))
            }}
          />
          {errors.email ? <p className="access-error" id="login-email-error">{errors.email}</p> : null}
        </div>
        <div className="access-field">
          <label htmlFor="login-password">Password</label>
          <div className="access-password-field">
            <input
              autoComplete="current-password"
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              aria-describedby={errors.password ? 'login-password-error' : undefined}
              aria-invalid={Boolean(errors.password)}
              onChange={(event) => {
                setPassword(event.target.value)
                if (errors.password) setErrors((current) => ({ ...current, password: undefined }))
              }}
            />
            <button
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="access-password-toggle"
              onClick={() => setShowPassword((current) => !current)}
              type="button"
            >
              <span aria-hidden="true" className="access-password-eye"><span /></span>
            </button>
          </div>
          {errors.password ? <p className="access-error" id="login-password-error">{errors.password}</p> : null}
        </div>
        <label className="access-check">
          <input checked={remember} type="checkbox" onChange={(event) => setRemember(event.target.checked)} />
          <span>Keep me signed in on this device.</span>
        </label>
        <InlineStatus result={result} />
        <div className="access-actions">
          <button className="access-primary" disabled={submitting} type="submit">
            {submitting ? 'Signing in…' : 'Sign in'}
            <ActionGlyph />
          </button>
          <button className="access-text-button" type="button" onClick={() => navigate('/auth/recovery')}>
            Need help signing in?
          </button>
          <button className="access-text-button" type="button" onClick={() => navigate('/auth/create')}>
            Create an account
          </button>
          <button className="access-text-button public-support-link" type="button" onClick={() => navigate('/support')}>
            Open Support
          </button>
        </div>
      </form>
    </AccountScaffold>
  )
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
